<script setup lang="ts">
  import { computed } from 'vue';
  import type { HTMLAttributes } from 'vue';

  import type { Task } from '@stuut/api/models';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';
  import { cn } from '@ui/utils/utils';

  import { ALERT_DAY_THRESHOLD, HIGH_ALERT_DAY_THRESHOLD } from '@/constants/constants';

  interface Props {
    alertDayThreshold?: number;
    compact?: boolean;
    gapClassName?: HTMLAttributes['class'];
    highAlertDayThreshold?: number;
    iconClassName?: HTMLAttributes['class'];
    showAnyTaskType?: boolean;
    task?: Task;
  }

  const props = withDefaults(defineProps<Props>(), {
    alertDayThreshold: ALERT_DAY_THRESHOLD,
    gapClassName: 'gap-x-1.5',
    highAlertDayThreshold: HIGH_ALERT_DAY_THRESHOLD,
  });

  const daysElapsed = computed(() => props.task?.daysElapsed ?? 0);

  const showAlert = computed(() => daysElapsed.value >= props.alertDayThreshold);

  const showHighAlert = computed(() => daysElapsed.value >= props.highAlertDayThreshold);
</script>

<template>
  <Tooltip v-if="showAlert" :disabled="!compact" :delay-duration="0">
    <TooltipTrigger class="focus:outline-none" :class="!compact && 'cursor-default'">
      <span
        v-if="task?.isOpen && (task?.isReplyRequired || showAnyTaskType) && showAlert"
        v-bind="$attrs"
        class="flex flex-1 items-center whitespace-nowrap"
        :class="gapClassName"
      >
        <i
          class="fa fa-circle-exclamation text-error"
          :class="cn(showHighAlert ? 'fas' : 'far', iconClassName)"
        />

        <slot
          v-bind="{
            daysElapsed,
            showAlert,
            showHighAlert,
            fromNow: task?.created_at.fromNow(true),
          }"
        />

        <span v-if="!$slots.default">
          <template v-if="compact">
            {{ daysElapsed }}
          </template>

          <template v-else> {{ task?.created_at.fromNow(true) }} without reply </template>
        </span>
      </span>
    </TooltipTrigger>

    <TooltipContent> {{ task?.created_at.fromNow(true) }} without reply </TooltipContent>
  </Tooltip>
</template>
